/**
 * Transforma textos do inventário (feature-map.csv) em linguagem para o usuário final.
 * Remove jargão de API, rotas internas, nomes de arquivo e termos de desenvolvimento.
 */

import { applyHelpGlossary } from "./helpGlossary.js";

function helpSurfaceVisible(surface, access) {
  const key = String(surface || "app").trim();
  if (!key || key === "app") return true;
  const has = (resource, action = "view") => Boolean(access?.scopeSet?.has?.(`${resource}.${action}`));
  if (key === "platform_console") return Boolean(access?.isPlatformAdmin);
  if (key === "voice_sms") return has("channels");
  if (key === "public_ecom") return has("payment_methods") || has("delivery_methods") || has("products");
  if (key === "public_websites") return has("contents");
  if (key === "public_surveys") return has("surveys");
  return false;
}

const INTERNAL_ROUTE = new Set(["(api)", "(api_only)", "(config)"]);

const DROP_LINE_PATTERNS = [
  /^component:\s*/i,
  /^rotas?:\s*/i,
  /^rota:\s*/i,
  /^backend\b/i,
  /^renderizado quando\b/i,
  /^persistência\b/i,
  /^salva em\b/i,
  /^put\s+\//i,
  /^get\s+\//i,
  /^post\s+\//i,
  /^api\b/i,
  /\/webhooks\//i,
  /localstorage/i,
  /exportdashboardcsv/i,
  /getbillingoffers/i,
  /xpro:billing/i,
  /identificador\s*=/i,
  /`tipo`/i,
  /\(ex\.:\s*phone=\{/i,
  /datagrid/i,
  /server-side/i,
  /\bsocket events\b/i,
  /\bS3\b/i,
  /\bMP3\b/i,
  /\bprovider\b/i,
  /\bdeliveryStatus\b/i,
  /\bplatformDeleteDeadline\b/i,
  /\bcanDeleteFromPlatform\b/i,
  /\bcanRetry\b/i,
  /\bbeforeMessageId\b/i,
  /\bunreadClient\b/i,
  /\blastSeen\b/i,
  /\bUI:\s*/i,
  /\bForm:\s*/i,
  /\bAção\s+'[^']+'\s+(abre|chama)/i,
  /\bPromise\.allSettled\b/i,
  /\bmutation\b/i,
  /\bhook\s+use/i,
  /\bDELETE\s+\//i,
  /\bGET\s+\//i,
  /\bPOST\s+\//i,
  /\bnavigate\s+\//i,
  /\bRoute param\b/i,
  /\bPAGE_SIZE\b/i,
  /\bloadMore\s*\(/i,
  /\brefreshAll\b/i,
  /\bvisibilitychange\b/i,
  /\bdrillDown\s*\(/i,
  /\bstage\./i,
  /\btypeType\b/i,
  /\bfocusedCardId\b/i,
  /\bcreateConversation\b/i,
  /\bConfirmDialog\b/i,
  /\bSurveyBuilderDialog\b/i,
  /\bAddProductForm\b/i,
  /\bAddClientForm\b/i,
  /\bToggle\s+'/i,
  /\bCTA leva para\b/i,
  /\bCTA:\s*/i,
  /\bDnD\b/i,
  /\breact-beautiful-dnd\b/i,
  /\bLinearProgress\b/i,
  /\bheurística\b/i,
  /\bTabs controlam\b/i,
  /\bsyncLegacyMutation\b/i,
  /\bcreateFromTemplateMutation\b/i,
  /\bmetricType\b/i,
  /\baudienceType\b/i,
  /\bXLSX\b/i,
  /\bnormalizeRow\b/i,
  /\bPasteDialog\b/i,
  /\bcurrentPrice\b/i,
  /\bsheetPrice\b/i,
  /\bdebounce\b/i,
  /\bUserTable\b/i,
  /\bChannelTable\b/i,
  /\bCompanyTable\b/i,
  /\bClientTable\b/i,
  /\bSurveyTable\b/i,
];

/** Títulos de funcionalidades — nomes internos ou técnicos → português claro. */
const FEATURE_TITLE_REPLACEMENTS = [
  [/Empty state \(sem etapas comerciais\)/i, "Quando não há etapas de venda"],
  [/Empty state \(sem níveis cadastrados\)/i, "Quando não há níveis configurados"],
  [/\bEmpty state\b/i, "Quando a lista está vazia"],
  [/Drilldown por etapa/i, "Ver subetapas de uma coluna"],
  [/Pipeline específico/i, "Detalhe de uma negociação no pipeline"],
  [/Atualizar cards \(soft refresh\)/i, "Atualizar o quadro"],
  [/Ver analytics da pesquisa/i, "Ver análise da pesquisa"],
  [/Breadcrumb de navegação por níveis/i, "Navegar entre níveis de etapas"],
  [/Abrir card específico via URL/i, "Abrir negociação pelo link"],
  [/Redirecionar para Chat quando for Relacionamento/i, "Abrir no Atendimento (relacionamento)"],
  [/Mover card entre etapas \(drag & drop\)/i, "Mover negócio entre etapas (arrastar)"],
  [/Mover cliente entre níveis \(drag & drop\)/i, "Mover cliente entre níveis (arrastar)"],
  [/Indicador de progresso da coluna/i, "Barra de progresso da coluna"],
  [/Toolbar de ações/i, "Barra de ações"],
  [/Cadastro Central:/i, "Catálogo central:"],
  [/Import\/export/i, "importar e exportar"],
  [/sync central/i, "sincronizar catálogo central"],
  [/sync linked/i, "sincronizar vinculados"],
  [/cross-sell/i, "produtos relacionados"],
  [/Gestão:/i, ""],
  [/Listar clientes \(tabela\)/i, "Listar clientes"],
  [/Listar canais \(tabela\)/i, "Listar canais"],
  [/Listar usuários \(tabela\)/i, "Listar usuários"],
  [/Listar empresas \(tabela\)/i, "Listar empresas"],
  [/Excluir em massa/i, "Excluir vários de uma vez"],
  [/soft refresh/i, "atualização suave"],
  [/drilldown/i, "explorar detalhes"],
];

/** Descrições prontas para funcionalidades cujo CSV só tem nota técnica. */
const FEATURE_USER_DESCRIPTIONS = {
  "Alternar visualização compacta/detalhada":
    "Use o interruptor para ver os cards em modo compacto (menos detalhes) ou detalhado (mais informações).",
  "Breadcrumb de navegação por níveis":
    "No topo, a trilha de navegação mostra em qual nível de etapas você está e permite voltar.",
  "Drilldown por etapa":
    "Clique no título de uma coluna que tenha subetapas para entrar no próximo nível.",
  "Atualizar cards (soft refresh)":
    "Atualize manualmente ou ao voltar para a aba — o quadro recarrega sem perder o que você já estava vendo.",
  "Atualizar o quadro":
    "Atualize manualmente ou ao voltar para a aba — o quadro recarrega sem perder o que você já estava vendo.",
  "Criar nova solicitação (Comercial)":
    "Crie uma nova oportunidade comercial direto do pipeline; o quadro atualiza após salvar.",
  "Mover card entre etapas (drag & drop)":
    "Arraste o card de uma coluna para outra. Se uma regra automática impedir, o sistema avisa.",
  "Mover negócio entre etapas (arrastar)":
    "Arraste o card de uma coluna para outra. Se uma regra automática impedir, o sistema avisa.",
  "Carregar mais cards por etapa":
    "Cada coluna mostra até 50 negócios por vez. Use Carregar mais no fim da coluna para ver os seguintes.",
  "Quando não há etapas de venda":
    "Aparece um aviso explicativo com botão para cadastrar tipos de atendimento em Configurações → Tipos.",
  "Empty state (sem etapas comerciais)":
    "Aparece um aviso explicativo com botão para cadastrar tipos de atendimento em Configurações → Tipos.",
  "Quando não há níveis configurados":
    "Aparece um aviso com botão para cadastrar os níveis em Configurações → Etapas dos níveis de consciência.",
  "Empty state (sem níveis cadastrados)":
    "Aparece um aviso com botão para cadastrar os níveis em Configurações → Etapas dos níveis de consciência.",
  "Abrir card específico via URL":
    "Ao abrir um link direto para uma negociação, o pipeline foca nesse card.",
  "Abrir negociação pelo link":
    "Ao abrir um link direto para uma negociação, o pipeline foca nesse card.",
  "Redirecionar para Chat quando for Relacionamento":
    "Negociações do tipo relacionamento abrem automaticamente no Atendimento.",
  "Abrir no Atendimento (relacionamento)":
    "Negociações do tipo relacionamento abrem automaticamente no Atendimento.",
  "Colunas por nível de consciência":
    "Cada coluna representa um nível; os clientes aparecem como cards que você pode mover.",
  "Mover cliente entre níveis (drag & drop)":
    "Arraste o card do cliente para outra coluna para mudar o nível de interesse.",
  "Mover cliente entre níveis (arrastar)":
    "Arraste o card do cliente para outra coluna para mudar o nível de interesse.",
  "Indicador de progresso da coluna":
    "Barra no topo de cada coluna que indica quantos clientes há em relação a uma referência.",
  "Barra de progresso da coluna":
    "Barra no topo de cada coluna que indica quantos clientes há em relação a uma referência.",
  "Gerenciar pipeline de vendas":
    "Quadro em colunas por etapa de venda; arraste cards, filtre e acompanhe negociações.",
  "Pipeline específico":
    "Visualize e explore os detalhes de uma negociação específica no pipeline.",
  "Detalhe de uma negociação no pipeline":
    "Visualize e explore os detalhes de uma negociação específica no pipeline.",
  "Listar clientes (tabela)":
    "Veja os clientes em tabela com páginas e carregamento automático.",
  "Buscar clientes":
    "Digite na busca para filtrar clientes pelo nome ou outros dados.",
  "Criar cliente":
    "Clique em Adicionar cliente e preencha o formulário.",
  "Editar cliente":
    "Abra o cadastro, altere os dados e salve.",
  "Excluir cliente":
    "Selecione excluir e confirme na janela de confirmação.",
  "Excluir clientes em massa":
    "Marque vários clientes na tabela e exclua de uma vez.",
  "Iniciar conversa para um cliente":
    "Crie uma nova solicitação de atendimento para o cliente e abra a conversa.",
  "Listar clientes":
    "Veja os clientes em tabela com páginas e carregamento automático.",
  "indicadores do recorte atual":
    "No topo, cartões mostram quantas pesquisas foram publicadas, concluídas e a nota média.",
  "Abas":
    "Use as abas Visão executiva, Modelos e Gestão para navegar na área de pesquisas.",
  "Importar legado":
    "Traz pesquisas e respostas do sistema anterior para esta conta.",
  "Criar nova pesquisa":
    "Crie um rascunho e abra o editor para montar as perguntas.",
  "Criar a partir de modelo de mensagem":
    "Escolha um modelo pronto, gere o rascunho e edite as perguntas no editor.",
  "Lista de modelos de mensagem":
    "Modelos prontos com etiquetas, duração estimada e quantidade de perguntas.",
  "Lista de modelo de mensagems":
    "Modelos prontos com etiquetas, duração estimada e quantidade de perguntas.",
  "Gestão: listar/paginar pesquisas":
    "Na aba Gestão, veja todas as pesquisas em tabela com busca e páginas.",
  "Gestão: editar pesquisa":
    "Abra o editor com a pesquisa carregada para alterar perguntas e configurações.",
  "Gestão: excluir pesquisa":
    "Remove a pesquisa da lista após confirmação; respostas antigas ficam guardadas para auditoria.",
  "Gestão: exclusão em massa":
    "Marque várias pesquisas e exclua de uma vez; o sistema informa se alguma não puder ser removida.",
  "Abrir analytics da pesquisa":
    "Abra a página de análise com resultados, respostas e resumos por pergunta.",
  "Ver análise da pesquisa":
    "Abra a página de análise com resultados, respostas e resumos por pergunta.",
  "Alternar visualização compacta ou detalhada":
    "Use o interruptor para ver os cards em modo compacto (menos detalhes) ou detalhado (mais informações).",
  "Navegar entre níveis de etapas":
    "No topo, a trilha de navegação mostra em qual nível de etapas você está e permite voltar.",
  "Ver subetapas de uma coluna":
    "Clique no título de uma coluna que tenha subetapas para entrar no próximo nível.",
  "Detalhe de uma negociação":
    "Visualize e explore os detalhes de uma negociação específica no pipeline.",
  "Indicadores do recorte atual":
    "No topo, cartões mostram quantas pesquisas foram publicadas, concluídas e a nota média.",
  "Abas da área de pesquisas":
    "Use as abas Visão executiva, Modelos e Gestão para navegar na área de pesquisas.",
  "Importar do sistema anterior":
    "Traz pesquisas e respostas do sistema anterior para esta conta.",
  "Criar a partir de modelo":
    "Escolha um modelo pronto, gere o rascunho e edite as perguntas no editor.",
  "Listar e buscar pesquisas":
    "Na aba Gestão, veja todas as pesquisas em tabela com busca e páginas.",
  "Excluir várias pesquisas":
    "Marque várias pesquisas e exclua de uma vez; o sistema informa se alguma não puder ser removida.",
};

const REPLACEMENTS = [
  [/API sugere insights\/chat de métricas por POST\.?/gi, "Você pode conversar com o assistente sobre os números da aba aberta."],
  [/Botão Exportar chama exportDashboardCsv\([^)]*\)\.?/gi, "Use o botão Exportar no topo da aba para baixar planilha CSV."],
  [/Campos: Data inicial e Data final no topo do dashboard\.?/gi, "Escolha data inicial e data final no topo do painel."],
  [/^período;\s*visões por domínio$/i, "Escolha o período no topo e navegue pelas abas para ver cada assunto."],
  [/^período$/i, "Escolha o período no topo do painel"],
  [/^visões por domínio$/i, "Cada aba mostra um assunto diferente"],
  [/Aparece quando getBillingOffers retorna sinal ou evento xpro:billing-limit-hit\.?/gi, "Aparece quando há aviso de limite ou cobrança do plano."],
  [/É marcação local da UI; não necessariamente ACK do provedor\.?/gi, "Marca como lido na sua tela; o cliente pode ainda não ter visto."],
  [/Só quando canDeleteFromPlatform e antes do platformDeleteDeadline\.?/gi, "Só enquanto ainda estiver dentro do prazo permitido para apagar."],
  [/line chart/gi, "gráfico de linhas"],
  [/bar chart/gi, "gráfico de barras"],
  [/composed chart/gi, "gráfico combinado"],
  [/pizza/gi, "gráfico circular"],
  [/sunburst/gi, "gráfico hierárquico"],
  [/treemap/gi, "mapa em blocos"],
  [/drilldown/gi, "explorar detalhes"],
  [/fullscreen/gi, "tela cheia"],
  [/\bKPIs\b/g, "indicadores"],
  [/\bGMV\b/g, "valor total vendido"],
  [/\bSKU(s)?\b/gi, "produto$1"],
  [/\bTMA\b/g, "tempo médio de atendimento"],
  [/\bTME\b/g, "tempo médio de espera"],
  [/\bCRUD\b/gi, "cadastrar, editar e remover"],
  [/\bOAuth\b/gi, "login autorizado"],
  [/conexão automática inbound/gi, "receber mensagens automaticamente"],
  [/webhook inbound/gi, "receber mensagens automaticamente"],
  [/\bwebhook(s)?\b/gi, "integração automática$1"],
  [/definir config\.?/gi, ""],
  [/\btemplate(s)?\b/gi, "modelo de mensagem$1"],
  [/\bwidget\b/gi, "chat no site"],
  [/\bsoftphone\b/gi, "telefone no navegador"],
  [/\bDataGrid\b/gi, "tabela"],
  [/\bstub\b/gi, "em preparação"],
  [/\bACK\b/g, "confirmação"],
  [/\bprovider\b/gi, "serviço"],
  [/WhatsApp via QR\s?Code \(.*?\)/gi, "Conecte escaneando um QR Code no celular"],
  [/AppMax — PIX \(API\)/gi, "AppMax — PIX"],
  [/AppMax — cartão \(API\)/gi, "AppMax — cartão"],
  [/via API/gi, ""],
  [/Meta Cloud API/gi, "Meta oficial"],
  [/QRCode/gi, "QR Code"],
  [/\bURI\b/gi, "endereço"],
  [/\bJSON\b/gi, "dados"],
  [/\bHTTP\b/gi, ""],
  [/\bPOST\b/g, ""],
  [/\bGET\b/g, ""],
  [/\bPUT\b/g, ""],
  [/\bDELETE\b/g, ""],
  [/—\s*Component:.+$/gi, ""],
  [/\(segmento\)/gi, ""],
  [/\baba:\s*/gi, ""],
  [/persistência localStorage/gi, "suas datas ficam salvas neste navegador"],
  [/insights IA/gi, "resumo automático"],
  [/Exportar Excel/gi, "exportar para Excel"],
  [/\bkanban\b/gi, "quadro em colunas"],
  [/\bswitch\b/gi, "interruptor"],
  [/\bdrag\s*&\s*drop\b/gi, "arrastar e soltar"],
  [/\bpageSize\b/gi, "itens por página"],
  [/\brefetch\b/gi, "atualizar lista"],
  [/\bloading\b/gi, "carregando"],
  [/\bpaginação\b/gi, "páginas"],
  [/\brollback\b/gi, "desfaz se falhar"],
  [/\botimista\b/gi, "atualização imediata"],
  [/\bexplicação;\s*CTA para tipos\b/gi, "mensagem explicativa e botão para cadastrar tipos"],
  [/\bexplicação;\s*CTA para cadastro\b/gi, "mensagem explicativa e botão para cadastrar"],
  [/\bCTA\b/gi, "botão de atalho"],
  [/\bscore médio\b/gi, "nota média"],
  [/\btop completion rate\b/gi, "maior taxa de respostas"],
  [/\bimport\/export\b/gi, "importar e exportar"],
  [/\bmodelo de mensagems\b/gi, "modelos de mensagem"],
  [/\bmodelo de mensagem(s)?\b/gi, "modelo de mensagem$1"],
  [/\bsync legado\b/gi, "importar do sistema anterior"],
  [/\bsegmentação\b/gi, "público-alvo"],
  [/\bpreview\b/gi, "pré-visualização"],
  [/\bfallback\b/gi, "alternativa"],
  [/\bimport planilha\b/gi, "importar planilha"],
  [/\bexport excel\b/gi, "exportar para Excel"],
  [/\bpré-validação\b/gi, "conferência antes de aplicar"],
  [/\bSKU_Principal\b/gi, "produto principal"],
  [/\bSKU_Relacionado\b/gi, "produto relacionado"],
  [/\bimport-export\b/gi, "importar e exportar"],
  [/\bsoft refresh\b/gi, "atualização sem recarregar a página inteira"],
  [/\bdrilldown por id\b/gi, "explorar detalhes de uma negociação"],
  [/\braiz;\s*trilha de etapas\b/gi, "volta ao início e navega entre níveis de etapas"],
  [/\bmanual;\s*auto ao focar\/voltar visibilidade\b/gi, "manualmente ou ao voltar para esta aba"],
  [/\bcriação rápida;\s*refresh após criar\b/gi, "criação rápida; o quadro atualiza depois"],
  [/\bpaginação por coluna\b/gi, "carrega mais negócios ao rolar ou clicar em Carregar mais"],
  [/\bfoca card\/popup quando suportado\b/gi, "destaca a negociação na tela"],
  [/\btypeType==1 redireciona\b/gi, "abre no Atendimento quando for relacionamento"],
  [/\bexplicação;\s*CTA para tipos\b/gi, "aviso com botão para Configurações → Tipos"],
  [/\betapas configuradas;\s*contagem por coluna\b/gi, "cada coluna mostra seus clientes e a quantidade"],
  [/\breordena;\s*persiste estágio\b/gi, "reorganiza e salva o novo nível"],
  [/\bbarra proporcional\b/gi, "barra de progresso"],
  [/\bmétricas por pergunta;\s*respostas;\s*insights\b/gi, "resultados por pergunta, respostas e resumos"],
  [/\bpublicadas;\s*concluídas;\s*score médio\b/gi, "quantas foram publicadas, concluídas e a nota média"],
  [/\bVisão executiva;\s*modelo de mensagems;\s*Gestão\b/gi, "Visão executiva, modelos e gestão"],
  [/\bcria rascunho;\s*abre builder\b/gi, "cria rascunho e abre o editor de perguntas"],
  [/\bseleção múltipla;\s*reporta falhas\b/gi, "marque vários itens; o sistema informa se algum não puder ser excluído"],
  [/\bseleção múltipla;\s*tenta remover todas\b/gi, "marque vários itens e exclua de uma vez"],
  [/\bexclusão lógica;\s*confirmação\b/gi, "remove da lista após confirmação (respostas ficam guardadas)"],
  [/\bnavega para\s*/gi, "abre "],
  [/\bgera slug;\s*exige company;\s*abre nova aba\b/gi, "gera link público e abre em nova aba"],
  [/\bcards;\s*tags;\s*duração;\s*total perguntas\b/gi, "cartões com etiquetas, duração e quantidade de perguntas"],
  [/\btabela com paginação e busca\b/gi, "tabela com páginas e campo de busca"],
  [/\babre builder com survey carregada\b/gi, "abre o editor com a pesquisa carregada"],
  [/\bimporta pesquisas \(e respostas\) do sistema legado\b/gi, "traz pesquisas antigas e respostas do sistema anterior"],
  [/\bselecionar modelo de mensagem;\s*gerar rascunho;\s*abrir builder\b/gi, "escolha um modelo, gere o rascunho e edite as perguntas"],
];

function isTechnicalInventoryText(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  if (DROP_LINE_PATTERNS.some((re) => re.test(t))) return true;
  if (/\b[a-z]+[A-Z][a-zA-Z0-9]*\b/.test(t)) return true;
  if (/\b\w+\([^)]*\)/.test(t) && /[A-Z_]|stage\.|typeType|loadMore|refreshAll/i.test(t)) return true;
  if (/\b(Empty state|PAGE_SIZE|visibilitychange|use[A-Z]|Mutation|allSettled)\b/i.test(t)) return true;
  if (/^UI:|^Form:|^Ação '/i.test(t)) return true;
  if (/\/[a-z][a-z0-9_/-]*:[a-z]/i.test(t)) return true;
  return false;
}

function applyReplacements(text) {
  let out = String(text || "").trim();
  if (!out) return "";
  REPLACEMENTS.forEach(([re, rep]) => {
    out = out.replace(re, rep);
  });
  out = applyHelpGlossary(out);
  return out.replace(/\s{2,}/g, " ").replace(/\s+([,.;])/g, "$1").trim();
}

function humanizeFeatureTitle(feature) {
  let out = String(feature || "").trim();
  FEATURE_TITLE_REPLACEMENTS.forEach(([re, rep]) => {
    out = out.replace(re, rep);
  });
  return applyReplacements(out);
}

function sanitizeLine(line) {
  let out = applyReplacements(line);
  out = out.replace(/^o que configurar \(específico\):\s*/i, "");
  out = out.replace(/\((?:GET|POST|PUT|DELETE)[^)]*\)/gi, "");
  out = out.replace(/\/[a-z0-9_/-]+/gi, "");
  out = out.replace(/\(\s*\/?\s*\)/g, "");
  out = out.replace(/\bAPI Fácil\b/gi, "conexão por QR Code");
  out = out.replace(/\s{2,}/g, " ").replace(/\s+([,.;])/g, "$1").replace(/\.+/g, ".").trim();
  if (isTechnicalInventoryText(out)) return "";
  return out;
}

function formatSemicolonPhrases(text) {
  const raw = String(text || "").trim();
  if (!raw || isTechnicalInventoryText(raw)) return "";

  const preprocessed = applyReplacements(raw);
  if (preprocessed && !preprocessed.includes(";")) return sanitizeLine(preprocessed);

  const parts = raw
    .split(";")
    .map((part) => sanitizeLine(part.trim()))
    .filter(Boolean)
    .filter((part) => part.length > 3);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];

  const imperative = parts.map((part) => {
    const lower = part.toLowerCase();
    if (lower === "criar instância") return "Crie a instância";
    if (lower === "escanear qr code") return "Escaneie o QR Code";
    if (lower === "acompanhar status") return "Acompanhe o status da conexão";
    if (lower === "kanban" || lower === "quadro em colunas") return "Quadro em colunas";
    if (lower === "mover") return "Arraste entre colunas";
    if (lower === "filtros") return "Use filtros";
    if (lower.startsWith("criar ")) return `Crie ${part.slice(6)}`;
    if (lower.startsWith("escanear ")) return `Escaneie ${part.slice(9)}`;
    if (lower.startsWith("acompanhar ")) return `Acompanhe ${part.slice(11)}`;
    if (lower.startsWith("validar ")) return `Valide ${part.slice(8)}`;
    if (lower.startsWith("conectar ")) return `Conecte ${part.slice(9)}`;
    return part.charAt(0).toUpperCase() + part.slice(1);
  });

  let result;
  if (imperative.length <= 4) {
    const last = imperative.pop();
    const prefix = imperative.join(", ");
    const lastPhrase = last.charAt(0).toLowerCase() + last.slice(1);
    result = prefix ? `${prefix} e ${lastPhrase}` : last;
  } else {
    result = imperative.join(". ");
  }

  if (!result.endsWith(".")) result += ".";
  return result.replace(/\.+/g, ".");
}

function cleanLines(text) {
  if (isTechnicalInventoryText(text)) return [];
  return String(text || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !DROP_LINE_PATTERNS.some((re) => re.test(line)))
    .map((line) => (line.includes(";") ? formatSemicolonPhrases(line) : sanitizeLine(line)))
    .filter(Boolean)
    .filter((line) => !isTechnicalInventoryText(line));
}

function lookupFeatureDescription(featureKey, displayFeature) {
  const raw = String(featureKey || "").trim();
  const display = String(displayFeature || "").trim();
  return FEATURE_USER_DESCRIPTIONS[raw] || FEATURE_USER_DESCRIPTIONS[display] || "";
}

export function humanizeHelpDescription(variants, notes, feature = "") {
  const preset = lookupFeatureDescription(feature, humanizeFeatureTitle(feature));
  if (preset) return preset;

  const variantText = formatSemicolonPhrases(variants);
  const variantLines = cleanLines(variants);
  const noteLines = isTechnicalInventoryText(notes) ? [] : cleanLines(notes);

  const fromVariants = variantLines.length > 0 ? variantLines.join(" ") : variantText;
  if (fromVariants && !isTechnicalInventoryText(fromVariants)) {
    const extra = noteLines.find((n) => n.length > 12 && !fromVariants.includes(n));
    if (extra) return `${fromVariants} ${extra}`.slice(0, 320);
    return fromVariants.slice(0, 320);
  }

  const fromNotes = noteLines.join(" ");
  if (fromNotes && !isTechnicalInventoryText(fromNotes)) return fromNotes.slice(0, 320);

  return variantText || "";
}

/** Itens só para inventário técnico — não mostrar na Central de Ajuda. */
export function isInternalHelpItem(item, access = null) {
  const route = String(item?.route || "").trim().toLowerCase();
  if (INTERNAL_ROUTE.has(route)) return true;

  const page = String(item?.page || "").trim().toLowerCase();
  if (page === "permissions") return true;

  const surface = String(item?.surface || "app").trim();
  if (access) {
    return !helpSurfaceVisible(surface, access);
  }
  if (surface && surface !== "app") return true;

  return false;
}

export function humanizeHelpFeature(feature) {
  let text = humanizeFeatureTitle(feature);
  text = text.replace(/^aba:\s*/i, "");
  text = text.replace(/\s*\([^)]*segmento[^)]*\)\s*/gi, " ");
  text = text.replace(/\s*—\s*/g, " — ");
  return text.trim() || "—";
}

export function humanizeHelpPermission(permissions) {
  const raw = String(permissions || "").trim();
  if (!raw) return "";
  if (/^[a-z_]+:(view|create|update|delete|manage)/i.test(raw)) {
    const [resource, action] = raw.split(":");
    const actionMap = {
      view: "visualizar",
      create: "criar",
      update: "editar",
      delete: "excluir",
      manage: "gerenciar",
    };
    const resourceMap = {
      dashboard: "o Dashboard",
      chat: "o Atendimento",
      channels: "os canais",
      clients: "os clientes",
      integrations: "as integrações",
      agents: "os agentes de IA",
      campaigns: "as campanhas",
      surveys: "as pesquisas",
      products: "os produtos",
      sales_pipeline: "o Pipeline de Vendas",
      awareness: "os Níveis de consciência",
      pipeline_stages: "as etapas do pipeline",
      awareness_stages: "as etapas dos níveis de consciência",
      companies: "as empresas",
      users: "os usuários",
      positions: "os cargos",
      types: "os tipos de atendimento",
      chatbot: "os chatbots",
      automation_rules: "as regras de automação",
      payment_methods: "as formas de pagamento",
      delivery_methods: "as formas de entrega",
      antifraud_methods: "o antifraude",
      coupons: "os cupons",
      categories: "as categorias",
      banners: "os banners",
      contents: "os conteúdos",
      billing: "o plano e faturamento",
      workspace_i18n: "os idiomas",
    };
    const act = actionMap[action] || action;
    const res = resourceMap[resource] || resource.replace(/_/g, " ");
    return `Precisa poder ${act} ${res}`;
  }
  return `Acesso: ${applyReplacements(raw)}`;
}

export function humanizeSemicolonList(text) {
  return String(text || "")
    .split(";")
    .map((part) => humanizeHelpFeature(part.trim()))
    .filter(Boolean);
}

export function prepareHelpItemForDisplay(item, access = null) {
  if (!item || isInternalHelpItem(item, access)) return null;
  const displayFeature = humanizeHelpFeature(item.feature);
  const displayDescription = humanizeHelpDescription(item.variants, item.notes, item.feature);
  return {
    ...item,
    displayFeature,
    displayDescription,
    displayPermission: humanizeHelpPermission(item.permissions),
  };
}

export function filterHelpItemsForUser(items, access = null) {
  return (items || []).map((item) => prepareHelpItemForDisplay(item, access)).filter(Boolean);
}
